import Phaser from "phaser";
import { CodexSection } from "@phaser/ui/codex/CodexSection";
import { GameDataCache } from "@phaser/core/cache/GameDataCache";
import { t } from "@/i18n/i18n";

interface CodexScrollViewOptions {
  x: number;
  y: number;
  width: number;
  height: number;

  // 발견한 몬스터 id 목록
  discoveredMonsterIds: Set<string>;
}

export class CodexScrollView extends Phaser.GameObjects.Container {
  private content!: Phaser.GameObjects.Container;
  private scrollY = 0;
  private minScrollY = 0;
  private maxScrollY = 0;
  private viewX: number;
  private viewY: number;
  private viewWidth: number;
  private viewHeight: number;

  constructor(scene: Phaser.Scene, options: CodexScrollViewOptions) {
    super(scene, options.x, options.y);
    scene.add.existing(this);

    this.viewX = options.x;
    this.viewY = options.y;
    this.viewWidth = options.width;
    this.viewHeight = options.height;

    this.content = scene.add.container(0, 0);
    this.add(this.content);

    // 마스크 설정
    const maskGraphics = scene.make.graphics({});
    maskGraphics.fillStyle(0xffffff, 1);
    maskGraphics.fillRect(options.x, options.y, options.width, options.height);
    this.setMask(maskGraphics.createGeometryMask());

    // 섹션 빌드 및 콘텐츠 높이 계산
    const contentHeight = this.buildSections(
      scene,
      options.width,
      options.discoveredMonsterIds,
    );

    this.minScrollY = Math.min(0, options.height - contentHeight);
    this.maxScrollY = 0;

    this.bindInput(scene);
  }

  // 섹션 빌드 및 콘텐츠 높이 계산
  private buildSections(
    scene: Phaser.Scene,
    width: number,
    discoveredMonsterIds: Set<string>,
  ) {
    let currentY = 0;
    const sectionGap = 32;

    // 스테이지 데이터 불러오기
    const allStageData = GameDataCache.getAllStageData();

    allStageData.forEach((stageData) => {
      const section = new CodexSection(scene, 0, currentY, {
        width,
        title: t(`stage.${stageData.stageId}.name`),
        monsters: stageData.monsters.map((monster) => ({
          id: monster.id,
          key: monster.textureKey,
          discovered: discoveredMonsterIds.has(monster.id),
        })),
      });

      this.content.add(section);
      currentY += section.contentHeight + sectionGap;
    });

    return currentY;
  }

  // 스크롤 입력 바인딩
  private bindInput(scene: Phaser.Scene) {
    scene.input.on(
      "wheel",
      (
        pointer: Phaser.Input.Pointer,
        _gameObjects: Phaser.GameObjects.GameObject[],
        _deltaX: number,
        deltaY: number,
      ) => {
        if (!this.isPointerInside(pointer)) return;
        this.setScroll(this.scrollY - deltaY * 0.8);
      },
    );

    let isDragging = false;
    let dragStartY = 0;
    let startScrollY = 0;

    // 터치/마우스 드래그로 스크롤
    scene.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (!this.isPointerInside(pointer)) return;

      isDragging = true;
      dragStartY = pointer.y;
      startScrollY = this.scrollY;
    });

    // 드래그 중일 때 포인터 이동에 따라 스크롤 위치 업데이트
    scene.input.on("pointermove", (pointer: Phaser.Input.Pointer) => {
      if (!isDragging || !pointer.isDown) return;

      const dy = pointer.y - dragStartY;
      this.setScroll(startScrollY + dy);
    });

    scene.input.on("pointerup", () => {
      isDragging = false;
    });
  }

  // 포인터가 뷰 영역 안에 있는지 확인
  private isPointerInside(pointer: Phaser.Input.Pointer) {
    return (
      pointer.x >= this.viewX &&
      pointer.x <= this.viewX + this.viewWidth &&
      pointer.y >= this.viewY &&
      pointer.y <= this.viewY + this.viewHeight
    );
  }

  // 스크롤 위치 업데이트 (최소/최대 범위 내로 클램프)
  private setScroll(nextY: number) {
    this.scrollY = Phaser.Math.Clamp(nextY, this.minScrollY, this.maxScrollY);
    this.content.y = this.scrollY;
  }
}
